import type { Slide } from '../supabase/client';
import type { ExtractedSlideContent } from './types';
import { getSlideConfig } from '../slides/slideRegistry';
import { sanitizeText } from './export-helpers';

/**
 * Extract normalized text content from any slide type for document generation.
 * Handles all 20 slide types defined in slideRegistry.
 */
export function extractSlideContent(slide: Slide, slideIndex: number): ExtractedSlideContent {
  const config = getSlideConfig(slide.type);
  const c = slide.content || {};
  const category = config?.category || 'content';
  const typeLabel = config?.label || slide.type;

  const base: ExtractedSlideContent = {
    slideNumber: slideIndex + 1,
    title: sanitizeText(c.heading || c.title || c.audioTitle || c.question || c.prompt || c.instruction || `Slide ${slideIndex + 1}`),
    type: slide.type,
    typeLabel,
    category,
    mainText: '',
  };

  switch (slide.type) {
    // ===== CONTENT SLIDES =====
    case 'text_image':
      base.mainText = sanitizeText(c.content);
      base.title = sanitizeText(c.heading) || base.title;
      if (c.imageUrl) base.mediaUrl = c.imageUrl;
      break;

    case 'video':
      base.mainText = sanitizeText(c.description);
      base.title = sanitizeText(c.description) || 'Video Slide';
      if (c.videoUrl) base.mediaUrl = c.videoUrl;
      base.secondaryText = c.videoUrl ? `Video URL: ${c.videoUrl}` : undefined;
      break;

    case 'pdf':
      base.mainText = sanitizeText(c.title) || 'PDF Document';
      if (c.pdfUrl) base.mediaUrl = c.pdfUrl;
      base.secondaryText = c.pdfUrl ? `PDF URL: ${c.pdfUrl}` : undefined;
      break;

    case 'web_content':
      base.mainText = sanitizeText(c.description);
      if (c.url) base.mediaUrl = c.url;
      base.secondaryText = c.url ? `URL: ${c.url}` : undefined;
      break;

    case 'field_trip':
      base.mainText = sanitizeText(c.description);
      base.secondaryText = c.location ? `Location: ${c.location}` : undefined;
      if (c.panoramaUrl) base.mediaUrl = c.panoramaUrl;
      break;

    case 'audio':
      base.title = sanitizeText(c.audioTitle) || 'Audio Slide';
      base.mainText = sanitizeText(c.transcript) || '';
      if (c.audioUrl) base.mediaUrl = c.audioUrl;
      base.secondaryText = c.transcript ? 'Transcript included' : undefined;
      break;

    case 'label_diagram':
      base.mainText = sanitizeText(c.instructions);
      if (c.imageUrl) base.mediaUrl = c.imageUrl;
      if (Array.isArray(c.labels) && c.labels.length > 0) {
        base.items = c.labels.map((l: any) => (typeof l === 'string' ? l : l.text || l.label || ''));
        base.secondaryText = `Labels: ${base.items.join(', ')}`;
      }
      base.points = c.points;
      break;

    // ===== ACTIVITY SLIDES =====
    case 'multiple_choice':
      base.mainText = sanitizeText(c.question);
      if (Array.isArray(c.options)) {
        const letters = ['A', 'B', 'C', 'D', 'E', 'F'];
        base.items = c.options.map((opt: string, i: number) => `${letters[i] || i + 1}) ${opt}`);
        base.secondaryText = base.items.join('\n');
      }
      if (c.correctAnswer !== undefined && Array.isArray(c.options)) {
        base.answerText = c.options[c.correctAnswer] || '';
      }
      base.points = c.points;
      break;

    case 'true_false':
      base.mainText = sanitizeText(c.question);
      base.answerText = c.correctAnswer === true ? 'True' : 'False';
      base.points = c.points;
      break;

    case 'open_ended':
      base.mainText = sanitizeText(c.question);
      base.secondaryText = c.placeholder ? `Placeholder: ${sanitizeText(c.placeholder)}` : undefined;
      break;

    case 'poll':
      base.mainText = sanitizeText(c.question);
      if (Array.isArray(c.options)) {
        base.items = c.options.map((opt: string, i: number) => `${i + 1}) ${opt}`);
        base.secondaryText = base.items.join('\n');
      }
      break;

    case 'fill_blanks':
      base.mainText = sanitizeText(c.sentence);
      base.secondaryText = sanitizeText(c.instruction);
      if (Array.isArray(c.answers)) {
        base.answerText = c.answers.join(', ');
        base.items = c.answers;
      }
      base.points = c.points;
      break;

    case 'matching_pairs':
      base.mainText = sanitizeText(c.instruction);
      if (Array.isArray(c.pairs)) {
        base.items = c.pairs.map((p: any) => `${p.left} → ${p.right}`);
        base.secondaryText = base.items.join('\n');
        base.answerText = base.items.join('; ');
      }
      base.points = c.points;
      break;

    case 'time_to_climb':
      base.title = sanitizeText(c.title) || 'Quiz Game';
      if (Array.isArray(c.questions)) {
        base.items = c.questions.map(
          (q: any, i: number) => `Q${i + 1}: ${sanitizeText(q.question)}`
        );
        base.mainText = base.items.join('\n');
        const answers = c.questions.map(
          (q: any, i: number) =>
            `Q${i + 1}: ${Array.isArray(q.options) ? q.options[q.correctAnswer] || '' : ''}`
        );
        base.answerText = answers.join('; ');
      }
      if (c.timeLimit) base.secondaryText = `Time limit: ${c.timeLimit}s`;
      base.points = c.points;
      break;

    case 'draw_it':
      base.mainText = sanitizeText(c.instructions);
      if (c.backgroundImageUrl) base.mediaUrl = c.backgroundImageUrl;
      base.points = c.points;
      break;

    case 'collaborate_board':
      base.mainText = sanitizeText(c.prompt);
      const features = [];
      if (c.allowDrawing) features.push('Drawing');
      if (c.allowText) features.push('Text');
      if (features.length) base.secondaryText = `Enabled: ${features.join(', ')}`;
      break;

    case 'word_cloud':
      base.mainText = sanitizeText(c.question);
      if (c.maxWords) base.secondaryText = `Max words: ${c.maxWords}`;
      break;

    case 'quiz':
      if (Array.isArray(c.questions)) {
        base.items = c.questions.map(
          (q: any, i: number) => `Q${i + 1}: ${sanitizeText(q.question)}`
        );
        base.mainText = base.items.join('\n');
        const quizAnswers = c.questions.map(
          (q: any, i: number) =>
            `Q${i + 1}: ${Array.isArray(q.options) ? q.options[q.correctAnswer] || '' : ''}`
        );
        base.answerText = quizAnswers.join('; ');
      }
      if (c.timeLimit) base.secondaryText = `Time limit: ${c.timeLimit}s`;
      base.points = c.points;
      break;

    case 'drag_drop':
      base.mainText = sanitizeText(c.instruction);
      if (Array.isArray(c.items) && c.items.length > 0) {
        base.items = c.items.map((item: any) => (typeof item === 'string' ? item : item.text || item.label || ''));
        base.secondaryText = `Items: ${base.items.join(', ')}`;
      }
      if (Array.isArray(c.targets) && c.targets.length > 0) {
        const targetNames = c.targets.map((t: any) => (typeof t === 'string' ? t : t.text || t.label || ''));
        base.secondaryText = (base.secondaryText ? base.secondaryText + '\n' : '') + `Targets: ${targetNames.join(', ')}`;
      }
      break;

    case 'sequencing':
      base.mainText = sanitizeText(c.instruction);
      if (Array.isArray(c.items) && c.items.length > 0) {
        base.items = c.items.map((item: any) => (typeof item === 'string' ? item : item.text || item.label || ''));
        base.secondaryText = `Items: ${base.items.join(', ')}`;
      }
      if (Array.isArray(c.correctOrder) && c.correctOrder.length > 0) {
        base.answerText = `Correct order: ${c.correctOrder.join(', ')}`;
      }
      base.points = c.points;
      break;

    default:
      base.mainText = JSON.stringify(c, null, 2);
  }

  return base;
}
